import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { accountType } from '../../store/selectors';

type TrialTextType = 'button' | 'subheading' | 'title' | 'callToAction';

type TrialTextKeys = {
    NO_UPGRADE: Record<TrialTextType, string | undefined>;
    UPGRADE_TO_STANDARD_TRIAL: Record<TrialTextType, string | undefined>;
    UPGRADE_TO_PRO_TRIAL: Record<TrialTextType, string | undefined>;
};

export const useTrialText = () => {
    const { formatMessage: translate } = useIntl();
    const EnablePricingV2 = useFeatureFlagValue(Features.EnablePricingV2.name);
    const accountPlan = useSelector(accountType);

    return useMemo(() => {
        const TEXT_KEYS = {
            NO_UPGRADE: {
                button: undefined, // No upgrade dialog displayed
                subheading: undefined, // No upgrade dialog displayed
                title: undefined, // No upgrade dialog displayed
                callToAction: translate({ id: 'CLIENT_CONTACT_SALES' }),
            },
            UPGRADE_TO_STANDARD_TRIAL: {
                button: translate({ id: 'CLIENT_START_STANDARD_TRIAL' }),
                subheading: translate({ id: 'CLIENT_UPGRADE_TO_STANDARD_SUBHEADING' }),
                title: translate({ id: 'CLIENT_UPGRADE_STANDARD_TRIAL' }),
                callToAction: translate({ id: 'CLIENT_UPGRADE' }),
            },
            UPGRADE_TO_PRO_TRIAL: {
                button: translate({ id: 'CLIENT_START_PRO_TRIAL' }),
                subheading: translate({ id: 'CLIENT_UPGRADE_TO_PRO_SUBHEADING' }),
                title: translate({ id: 'CLIENT_UPGRADE_PRO_TRIAL' }),
                callToAction: translate({ id: 'CLIENT_CONTACT_SALES' }),
            },
        } as const satisfies TrialTextKeys;

        if (EnablePricingV2) {
            switch (accountPlan) {
                case 'PRO-TRIAL':
                    return TEXT_KEYS.UPGRADE_TO_STANDARD_TRIAL;
                case 'FREEKIT':
                case 'COMMUNITY':
                case 'TRIAL':
                case 'STD-TRIAL':
                case 'TEST-TRIAL':
                case 'ENTERPRISE':
                case 'UNLICENSED':
                case 'PRO':
                default:
                    return TEXT_KEYS.NO_UPGRADE;
            }
        }

        return TEXT_KEYS.UPGRADE_TO_PRO_TRIAL;
    }, [ EnablePricingV2, accountPlan, translate ]);
};
